import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import GenOutboundInterfaceLogSerializer from "issara-sdk/types/GenOutboundInterfaceLogSerializer_apps_INF";

// API
import GenOutboundInterfaceLogList from "issara-sdk/apis/GenOutboundInterfaceLogList_apps_INF";
import GenOutboundInterfaceData from "issara-sdk/apis/GenOutboundInterfaceData_apps_INF";

export type Props = {

}

export type State = Partial<{
  // seq
  GenerateOutboundFreezeTimeSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    generateOutboundData: GenerateOutboundDataType;
    outboundList: GenOutboundInterfaceLogSerializer[];
    paginatedItems: PaginatedItemsType;
  }>
}>;

export type GenerateOutboundDataType = Partial<{
  freezeDate: string;
  task: string;
}>;

type PaginatedItemsType = Partial<{
  items: GenOutboundInterfaceLogSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence Type
type SeqState = {
  sequence: "GenerateOutboundFreezeTime";
  restart?: boolean;
  clear?: boolean;
};

// Action Type
type ActionType = 
  | { action: "FETCH"; }
  | { action: "CLEAR"; }
  | { action: "GENERATE"; };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  GenerateOutboundFreezeTimeSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

export const DataInitial = {};

export const CARD_NAME = "CardGenerateOutboundFreezeTime";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  GENERATE: "GENERATE",
  FETCH: "FETCH",
  CLEAR: "CLEAR",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const INIT_DATA: GenerateOutboundDataType = {
  freezeDate: "",
  task: "",
};

export const LIST_LIMIT = 20;

// * Hardcode
export const TaskOptions = [
  { key: 1, value: "outbound_interface_admission", text: "Outbound Interface Admission" },
  { key: 2, value: "outbound_interface_billing", text: "Outbound Interface Billing" },
  { key: 3, value: "outbound_interface_df", text: "Outbound Interface DF" },
  { key: 4, value: "outbound_interface_discharge", text: "Outbound Interface Discharge" },
  { key: 5, value: "outbound_interface_drug_dispensing", text: "Outbound Interface Drug Dispensing" },
  { key: 6, value: "outbound_interface_drug_ipd", text: "Outbound Interface Drug IPD" },
  { key: 7, value: "outbound_interface_drug_opd", text: "Outbound Interface Drug OPD" },
  { key: 8, value: "outbound_interface_drug_transfer", text: "Outbound Interface Drug Transfer" },
  { key: 9, value: "outbound_interface_ipd_diagnosis", text: "Outbound Interface IPD Diagnosis" },
  { key: 10, value: "outbound_interface_opd_diagnosis", text: "Outbound Interface OPD Diagnosis" },
  { key: 11, value: "outbound_interface_or", text: "Outbound Interface OR" },
  { key: 12, value: "outbound_interface_patient_record", text: "Outbound Interface Patient Record" },
  { key: 13, value: "outbound_interface_supply_dispensing", text: "Outbound Interface Supply Dispensing" }
] as const;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      GenerateOutboundFreezeTimeSequence: {
        ...state.GenerateOutboundFreezeTimeSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    () => Action(controller, { ...params, action: "FETCH" })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (contrller, params) => {
  const actionHandlers = {
    [ACTIONS.FETCH]: HandleFetchOutbound,
    [ACTIONS.GENERATE]: HandleGenerateOutbound,
    [ACTIONS.CLEAR]: HandleClearOutbound,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](contrller, params as any);
  }
};

const HandleFetchOutbound: Handler<Params<"FETCH">> = async (controller, params) => {
  const state = controller.getState();

  const paginatedItems = state.GenerateOutboundFreezeTimeSequence?.paginatedItems;
  const page = paginatedItems?.page || 0;
  const pageSize = paginatedItems?.pageSize || LIST_LIMIT;
  const offset = page * pageSize;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.FETCH]: "LOADING",
    }
  });

  let data = {
    limit: pageSize,
    offset,
  };

  const result = await GetOutboundLogData(controller, data);

  if (result?.items) {
    controller.setState({
      GenerateOutboundFreezeTimeSequence: {
        ...state.GenerateOutboundFreezeTimeSequence,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
        outboundList: result.items,
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.FETCH]: "SUCCESS",
      }
    });
  } else {
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "Error To Get Log Data!"
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.FETCH]: "ERROR",
      },
    });
  }
};

const HandleGenerateOutbound: Handler<Params<"GENERATE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GENERATE]: "LOADING",
    },
  });

  const generateOutboundData = state.GenerateOutboundFreezeTimeSequence?.generateOutboundData;

  const data = {
    freeze_date: generateOutboundData?.freezeDate,
    task: generateOutboundData?.task,
  };

  const [genResp, genErr] = await GenerateOutbound(controller, { data });

  if (genResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GENERATE]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: genResp?.detail,
      },
    });

    // Clear Generate Outbound Data
    HandleClearOutbound(controller, {} as any);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GENERATE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: genErr,
      },
    });
  }
};

const HandleClearOutbound: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  const paginatedItems = state.GenerateOutboundFreezeTimeSequence?.paginatedItems;
  const total = paginatedItems?.total ?? 0;
  const pageSize = paginatedItems?.pageSize ?? LIST_LIMIT;
  const pages = Math.ceil(total/pageSize);

  controller.setState({
    GenerateOutboundFreezeTimeSequence: {
      ...state.GenerateOutboundFreezeTimeSequence,
      generateOutboundData: {
        ...INIT_DATA,
      },
      outboundList: [],
      paginatedItems: {
        ...paginatedItems,
        total,
        pageSize,
        page: pages - 1,
      }
    },
  });

  // Re-fetch
  HandleFetchOutbound(controller, {} as any);
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetOutboundLogData: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await GenOutboundInterfaceLogList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GenerateOutbound: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await GenOutboundInterfaceData.post({
    apiToken: controller.apiToken,
    data: {
      ...params.data,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, err];
};